/**
 * Blockquote will render a blockquote and an optional author tag.
 *
 * We use custom markdown syntax to get this to work, if the editor
 * uses a block quote an includes a `@author <name>` in it that name will
 * be extracted into the author portion.
 *
 * Example:
 *
 * > Pullquote there's no law of physics that says we can’t live a lot longer
 * @author toadums
 */
import React from 'react';
import {ElementContent, ReactMarkdownProps} from 'react-markdown/lib/ast-to-react';

import {Link, Text} from '#component/base';

import {blockquote, blockquoteAuthor} from '../Markdown.css';

// The BlockQuote prop types are super confusing. ReactMarkdownProps seems to fullfil it though
export const Blockquote = ({node}: ReactMarkdownProps) => {
  // Note: We traverse the tree twice: once in `getAuthor` and once in `renderChild`.
  // It was much easier to understand the recursion when split up into these two functions
  // instead of doing it all in one.
  const {node: nodeWithoutAuthor, author} = getAuthor(node);

  // Note we render the children custom, instead of just using the `children` prop so that
  // we can manipulate them (eg: pull out the @author)
  return (
    <blockquote className={blockquote}>
      {renderChild(nodeWithoutAuthor)}
      {author && (
        <Text as="p" variant="label" className={blockquoteAuthor}>
          &mdash; {author}
        </Text>
      )}
    </blockquote>
  );
};

/**
 * Recursively render the child tree. The leaves of the tree are text elements, this is primarily
 * what will be rendered to the screen. They will be rendered in Text elements inside nodes with
 * `type === 'element'`, which render different HTML tags dependings on their `tagName` property.
 *
 * We render the children recursively to support the format that ReactMarkdown gives us. If you
 * have nested elements they will be children of the same root element.
 *
 * For example, this markdown string...
 *
 * Paragraph **with bold _and italic_**
 *
 * ... would yield a tree structure like this: ...
 *
 * {type: 'element', tagName: 'p', children: [
 *   {type: 'text', value: 'Paragraph},
 *   {type: 'element', tagName: 'strong', children: [
 *     {type: 'text', value: 'with bold'},
 *     {type: 'element', tagName: 'em', children: [
 *       {type: 'text', value: 'and italic}
 *     ]}
 *   ]}
 * ]}
 *
 * ... which will turn into HTML like:
 *
 * <p>
 *   Paragraph
 *   <strong>
 *     with bold
 *     <em>and italic</em>
 *   </strong>
 * </p>
 */
const renderChild = (node: ElementContent, parentIndex = 0, childIndex = 0): React.ReactNode => {
  const key = `${parentIndex}-${childIndex}`;

  // Render the leaves of the tree
  if (node.type === 'text') {
    return node.value;
  }

  // ignore any non-text or element nodes
  if (node.type !== 'element') {
    return null;
  }

  // Render the children recursively
  const children = node.children.map((child, i) => renderChild(child, parentIndex, i));

  // Special case for the root `blockquote` element. We want to render it ourselves above, but
  // it's easier to reason with the recursion if we always pass in an ElementContent
  if (node.tagName === 'blockquote') {
    return children;
  }

  // Special case to handle line breaks inside the blockquote
  if (node.tagName === 'br') {
    return <br key={key} />;
  }

  // Special case to render links inside the blockquote
  if (node.tagName === 'a' && node.properties?.href) {
    return (
      <Link href={node.properties.href} key={key}>
        <a>{children}</a>
      </Link>
    );
  }

  // All other cases will render a Text component as a `node.tagName` (p, strong, em, etc)
  // and recursively render all of that elements children.
  return (
    <Text as={node.tagName as React.ElementType} variant="heading2" color="eggplant" key={key}>
      {children}
    </Text>
  );
};

/**
 * `getAuthor` will traverse the node tree and remove the text-value corresponding to an author,
 * if one is presesnt. It will return the modified node tree and the author's name as a string.
 *
 * The node that you get back from `getAuthor` can be used just like the `root` node you pass in.
 * The only difference is that it will not include the author name string. This way you can render
 * the tree as normal, but render the `author` string with custom styling.
 */
const getAuthor = (root: ElementContent): {node: ElementContent; author?: string} => {
  let author: string | undefined;

  // Traverse the tree and look for an author element. This function will return a node almost
  // identical to the `root`. It will return nodes **unchanged** unless they are the author node.
  const traverseTree = (node: ElementContent): ElementContent => {
    // If the current element is a text element check to see if its value matches the author directive.
    if (node.type === 'text') {
      const authorName = isAuthor(node.value);
      if (authorName) {
        // Set the author name so we can return it later
        author = authorName;

        return {
          ...node,
          // Return an empty string so that we don't have to actually remove this element from the
          // tree. React will ignore the empty string when rendering. This is the only thing that
          // changes while we traverse the tree!
          value: '',
        };
      }
    }

    // If the node is an element recursively traverse its children
    if (node.type === 'element') {
      return {
        ...node,
        children: node.children.map(child => traverseTree(child)),
      };
    }

    return node;
  };

  const node = traverseTree(root);

  return {
    node,
    author,
  };
};

/**
 * Given a string figure out if it starts with the author directive (`@author `).
 *
 * If it does return the rest of the text after that directive to be used as the author name
 */
const isAuthor = (val: string) => {
  const match = val.match(/^(\@author\s)?(.+)/);
  return match?.[1] ? match[2] : null;
};
